import { useCallback, useEffect, useRef, useState } from 'react'
import { getReactions, setReaction, Chatv1Error, type ReactionRow } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { chatv1SelfUser } from '../chatv1/auth'

/**
 * REACTIONS FOR THE OPEN CONVERSATION — owner, 2026-10-17: Slack 這種使用 emoji 來回覆某一個信息.
 *
 * What is on each message: per message (by seq), the emoji in the order they were first put there,
 * each with the people who put it, oldest first. Three ways in, one shape:
 *   · a READ of the loaded messages' range (GET …/reactions), when the conversation opens and again
 *     when older messages page in — only the part not read yet;
 *   · a LIVE frame (`reaction`) for every change anyone makes, this person's other devices included;
 *   · a RE-READ of the whole loaded range on every socket (re)open: a frame missed while the socket
 *     was down never arrives late, it simply never arrives (the chat contract's reconcile-on-open).
 * Taking a reaction on or off is optimistic and put back if the cloud refuses.
 */
export type Reaction = { emoji: string; users: string[] }
export type ReactionsBySeq = Record<number, Reaction[]>

/** Pure: rows from a read → the per-message shape, emoji ordered by when each was first put on. */
export function fromRows(rows: ReactionRow[]): ReactionsBySeq {
  const sorted = [...rows].sort((a, b) => a.ts - b.ts)
  const out: ReactionsBySeq = {}
  for (const r of sorted) {
    const list = (out[r.seq] ??= [])
    const hit = list.find((x) => x.emoji === r.emoji)
    if (hit) {
      if (!hit.users.includes(r.user)) hit.users.push(r.user)
    } else list.push({ emoji: r.emoji, users: [r.user] })
  }
  return out
}

/** Pure and idempotent: one person's emoji on (or off) one message. A no-op returns `state` itself. */
export function applyReaction(state: ReactionsBySeq, seq: number, emoji: string, user: string, on: boolean): ReactionsBySeq {
  const list = state[seq] ?? []
  const hit = list.find((x) => x.emoji === emoji)
  if (on) {
    if (hit?.users.includes(user)) return state
    const next = hit
      ? list.map((x) => (x === hit ? { emoji, users: [...x.users, user] } : x))
      : [...list, { emoji, users: [user] }]
    return { ...state, [seq]: next }
  }
  if (!hit || !hit.users.includes(user)) return state
  const users = hit.users.filter((u) => u !== user)
  const next = users.length ? list.map((x) => (x === hit ? { emoji, users } : x)) : list.filter((x) => x !== hit)
  const out = { ...state }
  if (next.length) out[seq] = next
  else delete out[seq]
  return out
}

/** Pure: what a fresh read of [lo, hi] says replaces everything held for that range. */
export function replaceRange(state: ReactionsBySeq, lo: number, hi: number, read: ReactionsBySeq): ReactionsBySeq {
  const out: ReactionsBySeq = {}
  for (const [k, v] of Object.entries(state)) {
    const seq = Number(k)
    if (seq < lo || seq > hi) out[seq] = v
  }
  for (const [k, v] of Object.entries(read)) out[Number(k)] = v
  return out
}

/** The service reads at most 500 messages' worth at a time. */
const SPAN = 500

async function readRange(convId: string, lo: number, hi: number): Promise<ReactionsBySeq> {
  const rows: ReactionRow[] = []
  for (let a = lo; a <= hi; a += SPAN) {
    const r = await getReactions(convId, a, Math.min(hi, a + SPAN - 1))
    rows.push(...r.reactions)
  }
  return fromRows(rows)
}

/** What a refusal says to the person, in words. */
function refusal(e: unknown): string {
  const code = e instanceof Chatv1Error ? e.code : ''
  if (code === 'too_many_reactions') return 'This message already has as many different reactions as it can hold.'
  if (code === 'not_a_member') return 'You are no longer in this conversation.'
  if (code === 'no_such_message') return 'That message is no longer there.'
  return 'Could not save that reaction — try again.'
}

export function useReactions(convId: string | null, seqs: number[]) {
  const [bySeq, setBySeq] = useState<ReactionsBySeq>({})
  /** What is on screen now — a tap decides on/off from this, never from inside a state updater. */
  const shown = useRef(bySeq)
  shown.current = bySeq
  const [error, setError] = useState<{ seq: number; text: string } | null>(null)
  /** The range already read for this conversation; null until the first read. */
  const covered = useRef<{ lo: number; hi: number } | null>(null)
  const epoch = useRef(0)
  const lo = seqs.length ? Math.min(...seqs) : 0
  const hi = seqs.length ? Math.max(...seqs) : 0
  const range = useRef({ lo, hi })
  range.current = { lo, hi }

  // A different conversation: nothing carries over.
  useEffect(() => {
    epoch.current++
    covered.current = null
    setBySeq({})
    setError(null)
  }, [convId])

  const read = useCallback(
    (a: number, b: number) => {
      if (!convId || a < 1 || b < a) return
      const mine = epoch.current
      readRange(convId, a, b)
        .then((got) => {
          if (epoch.current !== mine) return
          setBySeq((prev) => replaceRange(prev, a, b, got))
        })
        .catch(() => {
          /* Not read: the chips stay as they were; the next open or page reads again. */
        })
    },
    [convId],
  )

  // Read only what is newly in view: the first page, then each older page as it arrives. Messages
  // that arrive LIVE are past `hi` of the last read and carry nothing yet — their reactions come as frames.
  useEffect(() => {
    if (!convId || !lo || !hi) return
    const c = covered.current
    if (!c) {
      covered.current = { lo, hi }
      read(lo, hi)
      return
    }
    if (lo < c.lo) read(lo, c.lo - 1)
    covered.current = { lo: Math.min(lo, c.lo), hi: Math.max(hi, c.hi) }
  }, [convId, lo, hi, read])

  // Live changes, and the re-read on every (re)open.
  useEffect(() => {
    if (!convId) return
    const offFrame = onChatv1Frame((f) => {
      if (f.type !== 'reaction' || f.conv_id !== convId) return
      setBySeq((prev) => applyReaction(prev, f.seq, f.emoji, f.user, f.on))
    })
    const offOpen = onChatv1Open(() => {
      const c = covered.current
      if (c) read(c.lo, Math.max(c.hi, range.current.hi))
    })
    return () => {
      offFrame()
      offOpen()
    }
  }, [convId, read])

  /** Put this emoji on the message, or take it off if it is already there from this person. */
  const toggle = useCallback(
    (seq: number, emoji: string) => {
      const me = chatv1SelfUser()
      if (!convId || !me || seq < 1) return
      const on = !(shown.current[seq] ?? []).some((x) => x.emoji === emoji && x.users.includes(me))
      setBySeq((prev) => applyReaction(prev, seq, emoji, me, on))
      setError(null)
      const mine = epoch.current
      setReaction(convId, seq, emoji, on).catch((e) => {
        if (epoch.current !== mine) return
        setBySeq((prev) => applyReaction(prev, seq, emoji, me, !on))
        setError({ seq, text: refusal(e) })
      })
    },
    [convId],
  )

  return { bySeq, toggle, error, self: chatv1SelfUser() }
}
